import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Clock, Loader2 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { CTABanner } from "../components/CTABanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Block = ({ text }) => {
  if (text.startsWith("## ")) {
    return <h2 className="font-display mt-10 mb-4 text-2xl font-bold text-white">{text.slice(3)}</h2>;
  }
  if (text.startsWith("### ")) {
    return <h3 className="font-display mt-8 mb-3 text-xl font-bold text-white">{text.slice(4)}</h3>;
  }
  if (text.startsWith("- ")) {
    return (
      <ul className="my-4 space-y-2.5">
        {text.split("\n").map((li, i) => (
          <li key={i} className="flex items-start gap-3 text-slate-300">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-dg-cyan" />
            {li.replace(/^- /, "")}
          </li>
        ))}
      </ul>
    );
  }
  return <p className="my-4 leading-relaxed text-slate-300">{text}</p>;
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then((r) => {
        setPost(r.data);
        document.title = `${r.data.title} — DG Hubs`;
        document.querySelector('meta[name="description"]')?.setAttribute("content", r.data.excerpt);
      })
      .catch(() => setMissing(true));
    return () => { document.title = "DG Hubs — Digital Marketing Agency in Delhi NCR"; };
  }, [slug]);

  return (
    <main data-testid="blog-post-page">
      <section className="relative overflow-hidden bg-dg-navy bg-grid-dark pt-36 pb-16 noise">
        <div className="absolute -top-40 left-1/2 h-96 w-[42rem] -translate-x-1/2 rounded-full bg-dg-blue/30 blur-[140px]" />
        <div className="relative mx-auto max-w-3xl px-5 sm:px-8">
          <button
            data-testid="back-to-blog-btn"
            onClick={() => navigate("/blog")}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-400 transition-colors hover:text-dg-cyan"
          >
            <ArrowLeft size={15} /> All Insights
          </button>
          {post && (
            <>
              <div className="mt-7 flex flex-wrap gap-2">
                {post.tags.map((t) => (
                  <span key={t} className="rounded-full border border-dg-cyan/25 bg-dg-cyan/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-dg-cyan">{t}</span>
                ))}
              </div>
              <h1 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.1] tracking-tight text-white">{post.title}</h1>
              <p className="mt-5 flex items-center gap-1.5 text-xs text-slate-500">
                <Clock size={13} /> {post.read_time} min read · {new Date(post.created_at).toLocaleDateString("en-IN", { dateStyle: "long" })}
              </p>
            </>
          )}
        </div>
      </section>

      <section className="bg-dg-navy pb-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          {post === null && !missing && (
            <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-dg-cyan" /></div>
          )}
          {missing && (
            <div className="py-20 text-center">
              <p className="text-slate-400">This article doesn't exist (anymore).</p>
              <Link to="/blog" className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-dg-cyan">Back to Insights <ArrowRight size={14} /></Link>
            </div>
          )}
          {post && (
            <Reveal>
              <article data-testid="blog-article" className="text-base sm:text-lg">
                <p className="border-l-2 border-dg-cyan pl-5 text-lg leading-relaxed text-slate-200 italic">{post.excerpt}</p>
                <div className="mt-8">
                  {post.content.split(/\n\s*\n/).map((b, i) => <Block key={i} text={b.trim()} />)}
                </div>
              </article>
            </Reveal>
          )}
        </div>
      </section>

      <CTABanner title="WANT THIS WORKING FOR YOUR BUSINESS?" sub="Reading about growth is step one. Step two is a free consultation with our team." />
    </main>
  );
}
